import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { BRAND, HERO_IMAGE, MISSION, VISION, CUSTOMERS } from "@/data/content";
import { MaskedLines, Reveal, Overline } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import CategoriesShowcase from "@/components/CategoriesShowcase";
import WhyChoose from "@/components/WhyChoose";
import Manifesto from "@/components/Manifesto";

const FaWhatsapp = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.887 9.884Z"/>
  </svg>
);

const HERO_HIGHLIGHTS = ["Premium Quality", "Trusted Brands", "Wholesale & Retail", "Fast Delivery", "Customer Satisfaction"];

function Hero() {
  const [start, setStart] = useState(false);
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 600], [0, 120]);
  const scaleImg = useTransform(scrollY, [0, 600], [1.05, 1.18]);

  useEffect(() => {
    const t = setTimeout(() => setStart(true), 150);
    return () => clearTimeout(t);
  }, []);

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0">
        <img src={HERO_IMAGE} alt="Luxury interior" className="h-full w-full object-cover brightness-[1.15]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-[#0a0a0a]/30" />
        <div className="absolute -left-40 top-1/4 h-[500px] w-[500px] rounded-full bg-[#e7c15a]/10 blur-[120px]" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 md:px-10 pt-28">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: start ? 1 : 0 }} transition={{ duration: 0.8 }}>
          <Overline>Welcome to {BRAND.legal}</Overline>
        </motion.div>

        <h1 className="mt-6 font-serif-lux text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.92] tracking-tight text-[#fafafa]">
          <MaskedLines
            start={start}
            lines={["Complete Interior", <span className="text-gold-gradient italic" key="s">Solutions</span>, "Under One Roof"]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: start ? 1 : 0, y: start ? 0 : 20 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-8 max-w-xl text-base md:text-lg text-[#b8b8b8] leading-relaxed"
        >
          Premium plywood, decorative laminates, digital door locks, designer handles, modular kitchen accessories, wardrobe fittings & complete interior solutions — for homes and commercial spaces across Kolkata.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: start ? 1 : 0, y: start ? 0 : 20 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link to="/products" data-testid="hero-products-btn" className="group relative overflow-hidden bg-[#e7c15a] px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium text-[#050505] flex items-center gap-2">
            <span className="relative z-10 flex items-center gap-2">View Products <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></span>
          </Link>
          <Link to="/contact?quote=1" data-testid="hero-quote-btn" className="group relative overflow-hidden border border-[#e7c15a] px-8 py-4 text-xs uppercase tracking-[0.2em] text-[#e7c15a]">
            <span className="relative z-10 transition-colors duration-300 group-hover:text-[#050505]">Request a Quote</span>
            <span className="absolute inset-0 -translate-x-full bg-[#e7c15a] transition-transform duration-300 group-hover:translate-x-0" />
          </Link>
          <a href={`tel:${BRAND.phoneRaw[0]}`} data-testid="hero-call-btn" className="border border-white/15 px-8 py-4 text-xs uppercase tracking-[0.2em] text-[#e8e8e8] hover:border-[#e7c15a] transition-colors flex items-center gap-2"><Phone size={14} /> Call Now</a>
          <a href={`https://wa.me/${BRAND.whatsapp}`} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="border border-[#25D366]/50 px-8 py-4 text-xs uppercase tracking-[0.2em] text-[#25D366] hover:bg-[#25D366] hover:text-[#050505] transition-colors flex items-center gap-2"><FaWhatsapp /> WhatsApp</a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: start ? 1 : 0 }}
          transition={{ duration: 0.8, delay: 1.3 }}
          className="mt-16 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/10 pt-6"
        >
          {HERO_HIGHLIGHTS.map((h) => (
            <span key={h} className="text-[11px] uppercase tracking-[0.2em] text-[#8a8a8a] flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-[#e7c15a]" /> {h}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function AboutMissionVision() {
  return (
    <section data-testid="about-preview" className="py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <div className="relative">
            <img src="/photos/brand/about.jpeg" alt="Vernika showroom" className="img-lux w-full aspect-[4/5] object-cover border border-white/10" />
            <div className="absolute -bottom-6 -right-4 md:-right-8 bg-[#0d0d0d] border border-[#e7c15a]/30 p-6 md:p-8 max-w-[240px]">
              <div className="font-serif-lux text-5xl text-gold-gradient">100%</div>
              <p className="text-xs uppercase tracking-[0.15em] text-[#b8b8b8] mt-2">Quality products & trusted brands</p>
            </div>
          </div>
        </Reveal>

        <div>
          <Reveal>
            <Overline>About Us</Overline>
            <h2 className="font-serif-lux mt-5 text-4xl md:text-5xl leading-tight tracking-tight">
              A premium interior solutions company, based in <span className="text-gold-gradient italic">Kolkata.</span>
            </h2>
            <p className="mt-6 text-[#b8b8b8] leading-relaxed">
              VERNIKA DESIGNS LLP provides complete interior products under one roof — premium plywood, laminates, modular kitchen accessories, wardrobe hardware, digital locks, luxury handles, bathroom accessories, architectural hardware and adhesives. We work with homeowners, architects, designers, builders and commercial projects across West Bengal.
            </p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 gap-px bg-white/5 border border-white/5">
            <Reveal className="bg-[#0a0a0a] p-7">
              <h3 className="font-serif-lux text-2xl text-[#e7c15a]">Our Mission</h3>
              <p className="mt-3 text-sm text-[#b8b8b8] leading-relaxed">{MISSION}</p>
            </Reveal>
            <Reveal delay={0.1} className="bg-[#0a0a0a] p-7">
              <h3 className="font-serif-lux text-2xl text-[#e7c15a]">Our Vision</h3>
              <p className="mt-3 text-sm text-[#b8b8b8] leading-relaxed">{VISION}</p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <Link to="/about" data-testid="about-more-btn" className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[#e7c15a] group">
              Discover Our Story <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Customers() {
  return (
    <section data-testid="customers-section" className="py-20 md:py-24 bg-[#080808] border-y border-white/5">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal><Overline>Customers We Serve</Overline></Reveal>
        <div className="mt-10 flex flex-wrap gap-x-4 gap-y-6">
          {CUSTOMERS.map((c, i) => (
            <Reveal key={c} delay={i * 0.04}>
              <span className="font-serif-lux text-2xl md:text-4xl text-[#4a4a4a] hover:text-[#e7c15a] transition-colors duration-500 cursor-default">
                {c}<span className="text-[#e7c15a] text-lg align-super"> ✦</span>
              </span>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaStrip() {
  return (
    <section data-testid="cta-strip" className="py-24 md:py-32 relative overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal className="relative border border-[#e7c15a]/30 bg-gradient-to-br from-[#0d0b06] to-[#050505] p-10 md:p-20 text-center">
          <Overline className="justify-center">Let&apos;s Build Together</Overline>
          <h2 className="font-serif-lux mt-5 text-4xl md:text-6xl leading-tight tracking-tight max-w-3xl mx-auto">
            Ready to bring your <span className="text-gold-gradient italic">interior vision</span> to life?
          </h2>
          <p className="mt-5 text-[#b8b8b8] max-w-xl mx-auto">Request a quote today or visit our showroom in Rajarhat, Kolkata for premium products and expert guidance.</p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link to="/contact?quote=1" data-testid="cta-quote-btn" className="bg-[#e7c15a] px-9 py-4 text-xs uppercase tracking-[0.2em] font-medium text-[#050505]">Request a Quote</Link>
            <Link to="/contact" data-testid="cta-contact-btn" className="border border-white/20 px-9 py-4 text-xs uppercase tracking-[0.2em] text-[#e8e8e8] hover:border-[#e7c15a] transition-colors">Contact Us</Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <EditorialMarquee />
      <AboutMissionVision />
      <CategoriesShowcase />
      <Manifesto />
      <WhyChoose />
      <Customers />
      <CtaStrip />
    </>
  );
}
